import { useEffect, useRef } from 'react';

/**
 * A custom hook that uses ResizeObserver to monitor element size changes
 * @param callback Function to call when size changes are detected
 * @returns A ref to attach to the element you want to observe
 */
export const useResizeObserver = (callback: (height: number) => void) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(0); // Stores the last height *actually reported* by the callback
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      
      if (entry) {
        const { height } = entry.contentRect;
        
        // If there's a pending timeout from a previous observation, it's now outdated.
        // Clear it regardless of the new height's relation to lastHeightRef.current.
        if (timeoutRef.current !== null) {
          window.clearTimeout(timeoutRef.current);
          timeoutRef.current = null; // Mark that the timeout is cleared
        }
        
        // Now, determine if this new height is significantly different from the last *reported* height.
        if (Math.abs(height - lastHeightRef.current) >= 5) {
          // Significant change found, schedule a new debounced callback for this height.
          const currentObservedHeight = height;
          timeoutRef.current = window.setTimeout(() => {
            lastHeightRef.current = currentObservedHeight; // Update to the height now being reported
            callback(currentObservedHeight);
            timeoutRef.current = null; // This timeout has finished
          }, 100);
        }
        // If the change is not significant (< 5px from last reported), do nothing further.
        // Any outdated pending callback has already been cleared.
      }
    });
    
    resizeObserver.observe(ref.current);
    
    const currentRef = ref.current;
    
    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef);
      }
      resizeObserver.disconnect();
      
      // Clean up any pending timeout when the component unmounts or callback changes
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [callback]);
  
  return ref;
};
